import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Popconfirm,
  Row,
  Table,
  Tooltip,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightOutlined,
  LoadingOutlined,
  DeleteOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import DashServices from "../../services/DashServices";
import { translate } from "../../locale";
import { TableHelper } from "../../helpers/TableHelper";
import Link from "antd/lib/typography/Link";
import PropTypes from "prop-types";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const SubscriptionsTable = ({ reload }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
    searchText: "",
  });
  const [total, setTotal] = useState(0);

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      sorter: { multiple: 2 },
      render: (text, record, index) => (
        <div className="flex space-x-2">
          <Tooltip title={translate("Ver suscripción")} color="blur">
            <Button
              type="text"
              icon={<ArrowRightOutlined />}
              size="small"
              className="border-0"
              onClick={() =>
                navigate(`/admin/subscriptions/${record.id}`, {
                  state: { enterprise: record.enterprise.id },
                })
              }
            />
          </Tooltip>
          <Popconfirm
            title={translate("¿Estás seguro de clonar el registro?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => handleClone(record.id)}
          >
            <Tooltip title={translate("Clonar")} color="blur">
              <Button
                type="text"
                icon={<CopyOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={translate("¿Estás seguro de eliminar el registro？")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Eliminar" color="blur">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
    {
      title: translate("Nombre"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Link
          onClick={() =>
            navigate(`/admin/subscriptions/${record.id}`, {
              state: { enterprise: record.enterprise.id },
            })
          }
          className="!text-black hover:!text-custom_primary"
        >
          {record.name}
        </Link>
      ),
      sorter: (a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      },
    },
    {
      title: translate("Empresa"),
      dataIndex: "enterprise",
      key: "enterprise",
      sorter: { multiple: 1 },
      render: (_, record) => (
        <Link
          onClick={() =>
            navigate(`/admin/companies/${record.enterprise.id}`, {
              state: { enterprise: record.enterprise.id },
            })
          }
          className="!text-black hover:!text-custom_primary"
        >
          {record.enterprise.name}
        </Link>
      ),
    },
  ];

  useEffect(() => {
    if (reload !== null && reload !== undefined) {
      loadData();
    }
  }, [reload]);

  useEffect(() => {
    loadData();
  }, [JSON.stringify(tableParams)]);

  const loadData = async () => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    if (tableParams.searchText !== "") {
      data = { ...data, name: tableParams.searchText };
    }
    setLoading(true);
    try {
      const response = await DashServices.getSubscriptions(null, data);
      if (response.status >= 200 && response.status < 300) {
        setData(response.data.subscriptions);
        setTotal(response.data.total_pages * tableParams.pagination.pageSize);
      } else {
        message.error(response.data.message, 10);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleClone = async (subscription_id) => {
    setLoading(true);
    try {
      const res = await DashServices.cloneSubscriptions(subscription_id);
      if (res.status >= 200 && res.status < 300) {
        loadData();
      } else {
        message.error(res.data.detail, 10);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleDelete = async (subscription_id) => {
    setLoading(true);
    try {
      const res = await DashServices.deleteSubscriptions(subscription_id);
      if (res.status >= 200 && res.status < 300) {
        loadData();
      } else {
        message.error(res.data.detail, 10);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      message.error(error.data.detail);
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const searchData = (value) => {
    setTableParams((v) => ({
      pagination: {
        ...v.pagination,
        current: 1,
      },
      searchText: value,
    }));
  };

  return (
    <>
      <Row gutter={16}>
        <Col span="6" className="mb-4" offset={18}>
          <Form>
            <Form.Item>
              <Input.Search
                allowClear
                placeholder={translate("Buscar")}
                onSearch={searchData}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={data}
        columns={columns}
        rowKey={(i) => i.id}
        loading={
          loading
            ? { indicator: antIcon, className: "text-custom_blue" }
            : false
        }
        locale={{
          emptyText: <Empty description={translate("No hay datos")} />,
          sortTitle: translate("Ordenar"),
          triggerDesc: translate("Ordenar (descendente)"),
          triggerAsc: translate("Ordenar (ascendente)"),
          cancelSort: translate("Cancelar orden"),
        }}
        pagination={{ ...tableParams.pagination, total: total }}
        onChange={handleTableChange}
      />
    </>
  );
};

SubscriptionsTable.propTypes = {
  reload: PropTypes.bool,
};

export default SubscriptionsTable;
